import React, { useState, useEffect, useCallback, useRef } from "react";
import { Auth } from "aws-amplify";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoading } from "../../../../context/LoadingContext";
import { jwtDecode } from "jwt-decode";
import config from "../../../../aws-exports";
import { getParameterFromManager } from "../../../../services/Utils";
import "../../../../styles/general.css";
import "../../../../styles/amtpl.css";
import SidebarCommon from "../../../../pages/Requirement/components/layout/AMTPLSidebar";
import Header from "../../../../pages/Requirement/components/layout/Header";
import DataTable from "../../../../pages/Requirement/components/common/ReusableTable";
import SearchFilter from "../../../../pages/Requirement/components/common/SearchFilter";
import {
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  fetchData,
  searchfetchQueries,
  postQuery,
} from "../../../../pages/Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../../../../pages/Requirement/components/common/AlerModel";

const ProductOfferCharges = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilter =
    location.state?.selectedFilter || "productofferscharges";
  const [activeUser, setActiveUser] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const { setLoading } = useLoading();
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [productoffercharges, setProductOfferCharges] = useState([]);
  const [INRToUsd, setINRToUSD] = useState(0.012);
  const [showForm, setShowForm] = useState(false);

  const [searchValues, setSearchValues] = useState({
    searchProduct: "",
    searchProductGrade:"",
  });
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editProductOfferChargesID, setEditProductOfferChargesID] =
    useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const formRef = useRef(null);

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  // Form state
  const [formData, setFormData] = useState({
    product: "",
    agropreneursCharges: "",
    sortingGradingCharges: "",
    financeOperationsCharges: "",
    minOfferQuantity: "",
  });

  // State for errors
  const [errors, setErrors] = useState({});

  const loadProductList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
          
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
            
          }),
        ]);

        // Filter distinct ports based on DestinationPortAdd commentMore actions
        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        console.log("@@distinctProducts", distinctProducts);

        setProductList(distinctProducts);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );


  // Handle input change for the form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Check required fields
    if (!formData.product) newErrors.product = "This field is required";
    if (!formData.agropreneursCharges)
      newErrors.agropreneursCharges = "This field is required";
    if (!formData.sortingGradingCharges)
      newErrors.sortingGradingCharges = "This field is required";
    if (!formData.financeOperationsCharges)
      newErrors.financeOperationsCharges = "This field is required";
    if (!formData.minOfferQuantity)
      newErrors.minOfferQuantity = "This field is required";

    // Check numeric fields for charges
    const chargesFields = [
      "agropreneursCharges",
      "sortingGradingCharges",
      "financeOperationsCharges",
    ];
    chargesFields.forEach((field) => {
      if (formData[field]) {
        const value = parseFloat(formData[field].trim());
        if (isNaN(value)) {
          newErrors[field] = "Must be a number";
        } else if (value < 0) {
          newErrors[field] = "Must be non-negative";
        } else if (value > 100) {
          newErrors[field] = "Must be less than or equal to 100";
        }
      }
    });

    // Validate minOfferQuantity as a positive number
    if (formData.minOfferQuantity) {
      const value = parseFloat(formData.minOfferQuantity.trim());
      if (isNaN(value)) {
        newErrors.minOfferQuantity = "Must be a number";
      } else if (value <= 0) {
        newErrors.minOfferQuantity = "Must be positive";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Simulate API call to save form data
  const saveFormData = async (data) => {
    setLoading(true);
    try {
      // Simulate API call with a 1-second delay
      let body = null;
      console.log("Saving data to API:", data);
      if (isEditMode) {
        body = {
          target: "updateProductOfferCharge",
          updateProductOfferChargeInput: {
            ProductOfferChargesID: data.ProductOfferChargesID,
            ProductID: data.product,
            AgropreneursCharges: data.agropreneursCharges,
            SortingGradingCharges: data.sortingGradingCharges,
            FinanceOperationsCharges: data.financeOperationsCharges,
            MinOfferQuantityMT: data.minOfferQuantity,
          },
        };
      } else {
        body = {
          target: "createProductOfferCharge",
          createProductOfferChargeInput: {
            ProductOfferChargesID: data.ProductOfferChargesID,
            ProductID: data.product,
            AgropreneursCharges: data.agropreneursCharges,
            SortingGradingCharges: data.sortingGradingCharges,
            FinanceOperationsCharges: data.financeOperationsCharges,
            MinOfferQuantityMT: data.minOfferQuantity,
          },
        };
      }

      console.log("Saving body to API:", body);
      if (body !== null) {
        const result = await postQuery(body);
        if (result) {
          setModal({
            show: true,
            type: "success",
            title: "Success",
            message: `Product offer charges ${
              isEditMode ? "updated" : "added"
            } successfully!`,
            autoClose: 2000,
          });
        }
      }

      // Refresh the table data
      await searchProductOfferCharges(searchValues, rowStart);
    } catch (error) {
      console.error("Error saving data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to ${
          isEditMode ? "update" : "add"
        } product offer charges.`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const dataToSave = {
        ...formData,
        ProductOfferChargesID: isEditMode
          ? editProductOfferChargesID
          : uuidv4(),
      };
      await saveFormData(dataToSave);
      setShowForm(false);
      setIsEditMode(false);
      setEditProductOfferChargesID(null);
      setSelectedRow(null);
      setFormData({
        product: "",
        agropreneursCharges: "",
        sortingGradingCharges: "",
        financeOperationsCharges: "",
        minOfferQuantity: "",
      });
    } else {
      console.log("Validation failed");
    }
  };

  // Handle form reset
  const handleReset = () => {
    setFormData({
      product: "",
      agropreneursCharges: "",
      sortingGradingCharges: "",
      financeOperationsCharges: "",
      minOfferQuantity: "",
    });
    setErrors({});
    setIsEditMode(false);
    setEditProductOfferChargesID(null);
    setSelectedRow(null);
    setShowForm(false);
  };

  const handleDataFilterChange = useCallback(
    (event) => {
      const selectedValue = event.target.value;
      setSelectedFilter(selectedValue);
      console.log("Selected filter:", selectedValue);
      if (
        selectedValue === "products" ||
        selectedValue === "offers" ||
        selectedValue === "entities"
      ) {
        navigate("/amtpl", { state: { selectedFilter: selectedValue } });
      } else if (selectedValue === "Add Requirement") {
        navigate("/amtpl/addrequirement", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Requirement Approval") {
        navigate("/amtpl/requirement", {
          state: { selectedValue },
        });
      } else if (selectedValue === "Product Offers") {
        navigate("/amtpl/productofferscharges", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Inland Shipping") {
        navigate("/amtpl/inlandshipping", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Country wise Product") {
        navigate("/amtpl/counrywiseproduct", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Product Category") {
        navigate("/amtpl/productcategory", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Product") {
        navigate("/amtpl/product", {
          state: { selectedFilter: selectedValue },
        });
      }
    },
    [navigate]
  );

  const searchProductOfferCharges = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);

      const body = {
        target: "listProductOfferChargeDetails",
        userOrder: "ProductID",
        rowStart: newRowStart,
        pageSize: pageSize,
        filterTerms: {
          ProductNamePartial: searchValue.searchProduct || "",
          Grade: searchValue.searchProductGrade || "",
        },
      };

      try {
        const response = await fetchData(body);
        setProductOfferCharges(response.items || response);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading listProductOfferCharges: ", error);
        setProductOfferCharges([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart]
  );

  const handleSearch = (searchTerm) => {
    console.log("Searching for:", searchTerm);
    searchProductOfferCharges(searchTerm);
    setRowStart(0);
  };

  const handleRowClick = useCallback(async (row) => {
    console.log("selected row::", row);
    setSelectedRow(row);
    setIsEditMode(true);
    setEditProductOfferChargesID(row.ProductOfferChargesID);
    setShowForm(true);
  }, []);

  // Populate form data when selectedRow changes
  useEffect(() => {
    if (selectedRow && isEditMode) {
      setFormData({
        product: selectedRow.ProductID || "",
        agropreneursCharges: selectedRow.AgropreneursCharges?.toString() || "",
        sortingGradingCharges:
          selectedRow.SortingGradingCharges?.toString() || "",
        financeOperationsCharges:
          selectedRow.FinanceOperationsCharges?.toString() || "",
        minOfferQuantity: selectedRow.MinOfferQuantityMT?.toString() || "",
      });
    }
  }, [selectedRow, isEditMode]);

  // Scroll to form when showForm is true
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showForm]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        } else {
          setActiveUser(user.attributes.email);
        }
        let inrToUsdVal = await getParameterFromManager("INR_TO_USD");
        inrToUsdVal = parseFloat(inrToUsdVal);
        setINRToUSD(inrToUsdVal);
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
    loadProductList();
  }, [navigate]);

  useEffect(() => {
    searchProductOfferCharges(searchValues);
  }, [searchProductOfferCharges,searchValues]);

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchProductOfferCharges(searchValues, newRowStart);
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const fields = [
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProduct: value }));
        loadProductGrades(value);
      },
      
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
      
    },
  ];

  const columns = [
    {
      id: "ProductName",
      numeric: false,
      label: "Product Name",
      // renderCell: (row) => {
      //   const item = productList.find((p) => p.ProductID === row.ProductID);
      //   return item ? item.ProductName : "";
      // },
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
      // renderCell: (row) => {
      //   const item = productList.find((p) => p.ProductID === row.ProductID);
      //   return item ? item.Grade : "";
      // },
    },
    {
      id: "AgropreneursCharges",
      numeric: true,
      label: "Agropreneurs Charges (in %)",
    },
    {
      id: "SortingGradingCharges",
      numeric: true,
      label: "Sorting Grading Charges (in %)",
    },
    {
      id: "FinanceOperationsCharges",
      numeric: true,
      label: "Finance Operations Charges (in %)",
    },
    {
      id: "MinOfferQuantityMT",
      numeric: true,
      label: "Min Offer Quantity (in MT)",
    },
  ];
const commonInputStyles = {
  '& .MuiInputBase-root': {
    height: '40px',
    fontSize: '14px',
    width: '100%',
    borderRadius: '8px', // Add border radius here
  },
  '& .MuiOutlinedInput-input': {
    padding: '10px 14px',
    fontSize: '14px',
  },
  width: '100%', // Ensure FormControl takes full width
};
  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <Header activeUser={activeUser} setLoading={setLoading} />

      <div className="content">
        <SidebarCommon
          selectedFilter={selectedFilter}
          handleDataFilterChange={handleDataFilterChange}
        />
        <main className="main-content">
          <div className="table-header">
            <div className="header-text">
              <div className="head-text">Product Offer Charges</div>
              <div className="head-subtext">
                
              </div>
            </div>
            <div
              className="head-btn"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div
                className="submit-btn"
                onClick={() => {
                  setShowForm(!showForm);
                  setIsEditMode(false);
                  setFormData({
                    product: "",
                    agropreneursCharges: "",
                    sortingGradingCharges: "",
                    financeOperationsCharges: "",
                    minOfferQuantity: "",
                  });
                }}
              >
                Add
              </div>
            </div>
          </div>
          <div className="search-filter">
            <div className="row">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
          </div>
          <div className="table-css">
            <DataTable
              columns={columns}
              data={productoffercharges}
              total={totalItems}
              title="Product Offer charges Information"
              rowsPerPage={pageSize}
              onRowClick={handleRowClick}
              onPageChange={handlePageChange}
              currentPage={currentPage}
            />
          </div>
           {showForm && (
            <div className="box-main">
          <div className="table-header">
            <div className="header-text">
              <div className="head-text">
                {/* {isEditMode ? "Edit" : "Add"}  */}
              </div>
            </div>
            <div className="head-btn">
             
                <>
                  <div onClick={handleSubmit} className="submit-btn">
                    {isEditMode ? "Update" : "Submit"}
                  </div>
                  <div onClick={handleReset} className="submit-btn">
                    {isEditMode ? "Cancel" : "Reset"}
                  </div>
                </>
             
            </div>
          </div>

         
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              style={{
                padding: "10px",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              <div className="row">
                {/* Product Dropdown */}
                <div className="col-md-4">
                  <FormControl
                    sx={{ minWidth: "100%" }}
                    error={!!errors.product}
                  >
                    <Typography variant="body2">Select Product</Typography>
                    <Select
                      name="product"
                      value={formData.product}
                      onChange={handleChange}
                      displayEmpty
                     sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      inputProps={{ "aria-label": "Select Product" }}
                      
                    >
                      <MenuItem value="" disabled>
                        Select Product
                      </MenuItem>
                      {productList.map((p) => (
                        <MenuItem key={p.ProductID} value={p.ProductID}>
                          {p.ProductName + " (" + p.Grade + ")"}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.product && (
                      <Typography variant="caption" color="error">
                        {errors.product}
                      </Typography>
                    )}
                  </FormControl>
                </div>

                {/* Agropreneurs Charges */}
                <div className="col-md-4">
                  <div className="mb-3">
                    <Typography variant="body2">
                      Agropreneurs Charges (%)
                    </Typography>
                    <TextField
                      type="text"
                      name="agropreneursCharges"
                      value={formData.agropreneursCharges}
                      onChange={handleChange}
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      error={!!errors.agropreneursCharges}
                      helperText={errors.agropreneursCharges}
                    />
                  </div>
                </div>

                {/* Sorting & Grading Charges */}
                <div className="col-md-4">
                  <div className="mb-3">
                    <Typography variant="body2">
                      Sorting & Grading Charges (%)
                    </Typography>
                    <TextField
                      type="text"
                      name="sortingGradingCharges"
                      value={formData.sortingGradingCharges}
                      onChange={handleChange}
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      error={!!errors.sortingGradingCharges}
                      helperText={errors.sortingGradingCharges}
                    />
                  </div>
                </div>

                {/* Finance & Operations Charges */}
                <div className="col-md-4">
                  <div className="mb-3">
                    <Typography variant="body2">
                      Finance & Operations Charges (%)
                    </Typography>
                    <TextField
                      type="text"
                      name="financeOperationsCharges"
                      value={formData.financeOperationsCharges}
                      onChange={handleChange}
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      error={!!errors.financeOperationsCharges}
                      helperText={errors.financeOperationsCharges}
                    />
                  </div>
                </div>

                {/* Min Offer Quantity MT */}
                <div className="col-md-4">
                  <div className="mb-3">
                    <Typography variant="body2">
                      Min Offer Quantity (MT)
                    </Typography>
                    <TextField
                      type="text"
                      name="minOfferQuantity"
                      value={formData.minOfferQuantity}
                      onChange={handleChange}
                      sx={{ ...commonInputStyles, backgroundColor: "white" }}
                      error={!!errors.minOfferQuantity}
                      helperText={errors.minOfferQuantity}
                    />
                  </div>
                </div>
              </div>
            </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProductOfferCharges;
